import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { AnonDeal, AnonSummary, MeResponse } from "../types";
import { PeriodPicker } from "../components/PeriodPicker";
import { resolvePeriod } from "../period";

/** Раздел для роли «Key analytic»: обезличенная аналитика. Все сущности —
 *  коды (CP-/CO-/BK-/OP-/AG-/SC-/D-NNNNNN), имён и текста нет. */

function fmt(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

export function KeyAnalyticPage({ me }: { me: MeResponse }) {
  const [period, setPeriod] = useState("this_month");
  const [customStart, setCustomStart] = useState<string | null>(null);
  const [customEnd, setCustomEnd] = useState<string | null>(null);
  const [summary, setSummary] = useState<AnonSummary | null>(null);
  const [deals, setDeals] = useState<AnonDeal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [ps, pe] = useMemo(
    () => resolvePeriod(period, customStart, customEnd),
    [period, customStart, customEnd],
  );

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (ps) p.set("periodStart", ps);
    if (pe) p.set("periodEnd", pe);
    return p.toString();
  }, [ps, pe]);

  useEffect(() => {
    if (period === "custom" && !(customStart && customEnd)) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<AnonSummary>(`/api/key-analytics/summary?${qs}`),
      api.get<{ deals: AnonDeal[] }>(`/api/key-analytics/deals?${qs}&limit=1000`),
    ])
      .then(([s, d]) => {
        setSummary(s);
        setDeals(d.deals);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [qs, period, customStart, customEnd]);

  if (!me.permissions.key_analytics) {
    return (
      <div className="text-tg-hint text-sm">
        Раздел доступен роли «Key analytic».
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold mb-1">📊 Аналитика (обезличенно)</h1>
      <p className="text-xs text-tg-hint mb-3">
        Все названия скрыты кодами. Доступны только цифры для анализа.
      </p>

      <div className="mb-4">
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(s, e) => {
            setCustomStart(s);
            setCustomEnd(e);
          }}
        />
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}
      {loading && <div className="text-tg-hint text-sm animate-pulse">Загрузка…</div>}

      {summary && (
        <section className="bg-tg-sectionBg rounded-xl p-4 mb-4">
          <div className="text-xs text-tg-hint">Оборот (импорт), тело платежей</div>
          <div className="mt-1 text-2xl font-semibold">
            {fmt(summary.total_body_rub)} ₽
          </div>
          <div className="text-sm text-tg-hint">
            вознаграждение {fmt(summary.total_fee_rub)} ₽ · средний%{" "}
            {summary.avg_fee_percent.toFixed(3)}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <Tile label="Заявок всего" value={String(summary.deals_total)} />
            <Tile label="Импортных" value={String(summary.import_count)} />
            <Tile label="Сред. размер" value={`${fmt(summary.avg_deal_size_rub)} ₽`} />
          </div>
          {Object.keys(summary.by_currency).length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-tg-hint mb-1">По валютам (шт.)</div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {Object.entries(summary.by_currency).map(([c, n]) => (
                  <span key={c} className="px-2 py-1 rounded bg-tg-bg">
                    {c}: <b>{n}</b>
                  </span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(summary.by_type).length > 0 && (
            <div className="mt-2">
              <div className="text-xs text-tg-hint mb-1">По типам (шт.)</div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {Object.entries(summary.by_type).map(([t, n]) => (
                  <span key={t} className="px-2 py-1 rounded bg-tg-bg">
                    {t}: <b>{n}</b>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <div className="space-y-2">
        <div className="text-xs text-tg-hint">Заявки (обезличенно)</div>
        {deals.map((d) => (
          <div key={d.deal_code} className="bg-tg-sectionBg rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm">{d.deal_code}</div>
              <div className="text-xs text-tg-hint">
                {d.type} · {d.deal_date || "—"}
              </div>
            </div>
            <div className="text-xs text-tg-hint mt-0.5">
              {d.counterparty_code || "—"} · {d.company_code || "—"}
              {d.bank_code ? ` · ${d.bank_code}` : ""}
            </div>
            <div className="text-sm mt-1">
              {fmt(d.amount)} {d.currency} × {fmt(d.rate)} ={" "}
              <b>{fmt(d.body_rub)} ₽</b> · {fmt(d.fee_percent)}% ={" "}
              {fmt(d.fee_rub)} ₽ · <span className="text-tg-hint">{d.status}</span>
            </div>
          </div>
        ))}
        {!loading && deals.length === 0 && (
          <div className="text-tg-hint text-sm">За период заявок нет.</div>
        )}
      </div>

      <ReportsSection periodStart={ps} periodEnd={pe} />
    </div>
  );
}

interface TemplateInfo {
  kind: string;
  label: string;
  description: string;
}

/** Стандартные отчёты (обезличенно): шаблоны + простой конструктор.
 *  Файлы скачиваются через стандартный механизм grant (analytics_export). */
function ReportsSection({
  periodStart, periodEnd,
}: {
  periodStart?: string;
  periodEnd?: string;
}) {
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [metrics, setMetrics] = useState<Record<string, string>>({});
  const [groups, setGroups] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [metric, setMetric] = useState("rub_volume");
  const [groupX, setGroupX] = useState("counterparty");
  // Дата суточного комплекта (по умолчанию — сегодня).
  const [eodDate, setEodDate] = useState<string>(
    () => new Date().toISOString().slice(0, 10),
  );

  useEffect(() => {
    api
      .get<{ templates: TemplateInfo[]; metrics: Record<string, string>; group_keys: Record<string, string> }>(
        "/api/key-analytics/report-templates",
      )
      .then((r) => {
        setTemplates(r.templates);
        setMetrics(r.metrics);
        setGroups(r.group_keys);
      })
      .catch(() => undefined);
  }, []);

  const periodBody = () => {
    if (periodStart && periodEnd) {
      return { periodKey: "custom", customStart: periodStart, customEnd: periodEnd };
    }
    return { periodKey: "this_month" };
  };

  const downloadExport = async (eid: number, name: string) => {
    if (!eid) {
      setMsg("Отчёт сформирован, но не найден для скачивания.");
      return;
    }
    await api.download("analytics_export", { export_id: eid }, name);
  };

  const runTemplate = async (t: TemplateInfo) => {
    setBusy(t.kind);
    setMsg(null);
    try {
      const res = await api.post<{ id: number }>(
        `/api/key-analytics/report/template/${t.kind}`,
        periodBody(),
      );
      await downloadExport(res.id, `${t.kind}.xlsx`);
      setMsg(`✅ «${t.label}» сформирован.`);
    } catch (e) {
      setMsg((e as ApiError).message);
    } finally {
      setBusy(null);
    }
  };

  const runConstructor = async () => {
    setBusy("constructor");
    setMsg(null);
    try {
      const res = await api.post<{ id: number }>(
        "/api/key-analytics/report/constructor",
        { ...periodBody(), metric, groupX },
      );
      await downloadExport(res.id, "constructor.xlsx");
      setMsg("✅ Конструктор сформирован.");
    } catch (e) {
      setMsg((e as ApiError).message);
    } finally {
      setBusy(null);
    }
  };

  const runEod = async () => {
    setBusy("eod");
    setMsg(null);
    try {
      const q = eodDate ? `?date=${eodDate}` : "";
      const res = await api.post<{ date: string; reports: { id: number; title: string }[] }>(
        `/api/key-analytics/eod-reports${q}`,
        {},
      );
      for (const r of res.reports) {
        // eslint-disable-next-line no-await-in-loop
        await downloadExport(r.id, `${r.title}.xlsx`);
      }
      setMsg(`✅ Суточный комплект за ${res.date} сформирован (${res.reports.length}).`);
    } catch (e) {
      setMsg((e as ApiError).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold mb-1">📑 Стандартные отчёты</h2>
      <p className="text-xs text-tg-hint mb-3">
        Excel-выгрузки в обезличенном виде (коды вместо имён).
      </p>

      {msg && (
        <div className="mb-3 p-2 rounded-lg bg-tg-sectionBg text-sm">{msg}</div>
      )}

      {/* Принудительный вызов суточного комплекта (как у админа/финрука) */}
      <section className="bg-tg-sectionBg rounded-xl p-3 mb-3">
        <div className="text-sm font-medium mb-1">Отчёты по итогам дня</div>
        <div className="text-xs text-tg-hint mb-2">
          Реестр-фикс + Поступления RUB + Потребность в валюте — тот же
          комплект, что получают админ и финрук, но обезличенно.
        </div>
        <label className="text-xs text-tg-hint">Дата</label>
        <div className="flex gap-2 mt-1">
          <input
            type="date"
            value={eodDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setEodDate(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg bg-tg-bg border border-tg-secondaryBg text-sm"
          />
          <button
            disabled={busy === "eod" || !eodDate}
            onClick={runEod}
            className="px-4 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50 whitespace-nowrap"
          >
            {busy === "eod" ? "Формирую…" : "📥 Сформировать"}
          </button>
        </div>
      </section>

      {/* Конструктор */}
      <section className="bg-tg-sectionBg rounded-xl p-3 mb-3">
        <div className="text-sm font-medium mb-2">Конструктор</div>
        <div className="flex flex-col gap-2">
          <label className="text-xs text-tg-hint">Показатель</label>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="px-3 py-2 rounded-lg bg-tg-bg border border-tg-secondaryBg text-sm"
          >
            {Object.entries(metrics).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <label className="text-xs text-tg-hint">Группировка</label>
          <select
            value={groupX}
            onChange={(e) => setGroupX(e.target.value)}
            className="px-3 py-2 rounded-lg bg-tg-bg border border-tg-secondaryBg text-sm"
          >
            {Object.entries(groups).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            disabled={busy === "constructor"}
            onClick={runConstructor}
            className="mt-1 px-4 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
          >
            {busy === "constructor" ? "Формирую…" : "📥 Сформировать"}
          </button>
        </div>
      </section>

      {/* Готовые шаблоны */}
      <div className="space-y-2">
        {templates.map((t) => (
          <div key={t.kind} className="bg-tg-sectionBg rounded-xl p-3">
            <div className="font-medium text-sm">{t.label}</div>
            <div className="text-xs text-tg-hint mt-0.5">{t.description}</div>
            <button
              disabled={busy === t.kind || t.kind === "counterparty_seasonality"}
              onClick={() => runTemplate(t)}
              className="mt-2 px-3 py-1.5 rounded-lg bg-tg-bg text-xs font-medium disabled:opacity-50"
              title={
                t.kind === "counterparty_seasonality"
                  ? "Требует выбора контрагента — недоступно в обезличенном режиме"
                  : undefined
              }
            >
              {busy === t.kind ? "Формирую…" : "📥 Сформировать"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-tg-bg rounded-lg p-2">
      <div className="text-tg-hint">{label}</div>
      <div className="font-medium mt-0.5 truncate">{value}</div>
    </div>
  );
}
